import { Button, Callout, DateField, Modal, TextArea, TextField } from '@humanforce/mango-ui-react';
import { useEffect } from 'react';
import type { PayRunDetailDto, PayRunFieldError } from './payRunsApiTypes';
import { formatSystemValue } from './payRunsModel';

interface EditPayRunModalProps {
  payRun: PayRunDetailDto;
  visible: boolean;
  displayLabel: string;
  notes: string;
  correctedEftDate: string;
  correctedPaymentDate: string;
  busy: boolean;
  actionAllowed: boolean;
  error: string | null;
  fieldErrors: PayRunFieldError[];
  onDisplayLabelChange: (value: string) => void;
  onNotesChange: (value: string) => void;
  onCorrectedEftDateChange: (value: string) => void;
  onCorrectedPaymentDateChange: (value: string) => void;
  onSave: () => void;
  onClose: () => void;
}

interface CancelPayRunModalProps {
  payRun: PayRunDetailDto;
  visible: boolean;
  busy: boolean;
  actionAllowed: boolean;
  error: string | null;
  onConfirm: () => void;
  onClose: () => void;
}

const date = (value: string): string =>
  new Intl.DateTimeFormat('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${value}T00:00:00Z`));

const fieldError = (errors: PayRunFieldError[], field: string) =>
  errors.find((candidate) => candidate.field.toLowerCase() === field.toLowerCase())?.message;

// Mango 0.51.0 renders its header inside the dialog but does not bind that
// heading to role="dialog". Keep this feature-local until Mango provides a
// public accessible-name prop or fixes the header association upstream.
export const useMangoDialogHeadingBridge = (visible: boolean, testId: string) => {
  useEffect(() => {
    if (!visible) return undefined;

    const bindHeading = () => {
      const dialog = document.querySelector<HTMLElement>(`[data-testid="${testId}"][role]`);
      const heading = dialog?.querySelector<HTMLElement>('h2');
      if (!dialog || !heading) return false;
      const headingId = `${testId}-heading`;
      heading.id = headingId;
      dialog.setAttribute('aria-labelledby', headingId);
      return true;
    };

    if (bindHeading()) return undefined;
    const observer = new MutationObserver(() => {
      if (bindHeading()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [testId, visible]);
};

export const useMangoFieldErrorBridge = (error: string | null | undefined, fieldId: string, testId: string) => {
  useEffect(() => {
    const field = document.getElementById(fieldId);
    const status = document.querySelector<HTMLElement>(`[data-testid="${testId}"] .__mgo-form-status-text`);
    const statusId = `${fieldId}-error`;
    if (!field) return;
    if (!error || !status) {
      if (field.getAttribute('aria-describedby') === statusId) field.removeAttribute('aria-describedby');
      return;
    }
    status.id = statusId;
    field.setAttribute('aria-describedby', statusId);
  }, [error, fieldId, testId]);
};

export const EditPayRunModal = ({
  payRun,
  visible,
  displayLabel,
  notes,
  correctedEftDate,
  correctedPaymentDate,
  busy,
  actionAllowed,
  error,
  fieldErrors,
  onDisplayLabelChange,
  onNotesChange,
  onCorrectedEftDateChange,
  onCorrectedPaymentDateChange,
  onSave,
  onClose,
}: EditPayRunModalProps) => {
  useMangoDialogHeadingBridge(visible, 'edit-pay-run-dialog');
  const displayLabelError = fieldError(fieldErrors, 'displayLabel');
  const notesError = fieldError(fieldErrors, 'notes');
  const eftError = fieldError(fieldErrors, 'eftReleaseDate');
  const paymentError = fieldError(fieldErrors, 'paymentDate');
  const needsDateCorrection = Boolean(payRun.priorRunId && payRun.eftApplicability === 'unknown');
  const dateError = needsDateCorrection && (!correctedEftDate || !correctedPaymentDate || correctedEftDate > correctedPaymentDate)
    ? 'Enter an EFT Release Date on or before the Payment Date.' : null;
  const localDisplayLabelError = displayLabel.length > 200 ? 'Display Label must be 200 characters or fewer.' : null;
  const localNotesError = notes.length > 500 ? 'Notes must be 500 characters or fewer.' : null;
  const effectiveDisplayLabelError = displayLabelError || localDisplayLabelError;
  const effectiveNotesError = notesError || localNotesError;
  useMangoFieldErrorBridge(effectiveDisplayLabelError, 'edit-pay-run-display-label', 'edit-pay-run-display-label-field');
  useMangoFieldErrorBridge(effectiveNotesError, 'edit-pay-run-notes', 'edit-pay-run-notes-field');
  const invalid = Boolean(localDisplayLabelError || localNotesError || dateError);

  return (
    <Modal
      visible={visible}
      testId="edit-pay-run-dialog"
      header="Edit Pay Run"
      size="large"
      closable={!busy}
      dismissableMask={!busy}
      closeOnEscape={!busy}
      onHide={() => { if (!busy) onClose(); }}
      footer={(
        <>
          <Button label="Cancel" variant="secondary" disabled={busy} onClick={onClose} />
          <Button label="Save changes" loading={busy} disabled={invalid || !actionAllowed} onClick={onSave} />
        </>
      )}
    >
      <div className="mgo-stack mgo-gap-l">
        {error && <div role="alert"><Callout tone="critical" title="Pay Run could not be updated">{error}</Callout></div>}
        {needsDateCorrection && <>
          <Callout tone="caution" title="Correct the successor dates">The scheduled EFT Release Date has passed. Choose an eligible date before calculating or progressing this Pay Run.</Callout>
          <div className="mgo-grid mgo-grid-cols-1 mgo-grid-cols-tablet-2 mgo-gap-m">
            <DateField label="EFT Release Date" dateFormat="ymd" fullWidth value={correctedEftDate}
              status={eftError || dateError ? 'critical' : undefined} statusText={eftError || dateError || undefined}
              onChange={(value) => onCorrectedEftDateChange(typeof value === 'string' ? value : '')} />
            <DateField label="Payment Date" dateFormat="ymd" fullWidth value={correctedPaymentDate}
              status={paymentError ? 'critical' : undefined} statusText={paymentError || undefined}
              onChange={(value) => onCorrectedPaymentDateChange(typeof value === 'string' ? value : '')} />
          </div>
        </>}
        <div className="mgo-grid mgo-grid-cols-1 mgo-grid-cols-tablet-2 mgo-gap-m">
          <TextField
            id="edit-pay-run-display-label"
            testId="edit-pay-run-display-label-field"
            label="Display Label"
            value={displayLabel}
            status={(displayLabelError || localDisplayLabelError) ? 'critical' : undefined}
            statusText={displayLabelError || localDisplayLabelError || undefined}
            onChange={(event) => onDisplayLabelChange(event.target.value)}
          />
          <TextArea
            id="edit-pay-run-notes"
            testId="edit-pay-run-notes-field"
            label="Notes"
            value={notes}
            rows={4}
            resize="vertical"
            characterLimit={500}
            status={(notesError || localNotesError) ? 'critical' : undefined}
            statusText={notesError || localNotesError || undefined}
            onChange={(event) => onNotesChange(event.target.value)}
          />
        </div>
        <section aria-label="Read-only Pay Run details" className="mgo-stack mgo-gap-s">
          <h3 className="mgo-text-m mgo-weight-strong">Pay Run details</h3>
          <dl className="mgo-grid mgo-grid-cols-1 mgo-grid-cols-tablet-2 mgo-gap-m">
            <div><dt className="mgo-text-xs mgo-color-muted">Pay Run ID</dt><dd>{payRun.payId}</dd></div>
            <div><dt className="mgo-text-xs mgo-color-muted">Business Record</dt><dd>{payRun.businessRecordName}</dd></div>
            <div><dt className="mgo-text-xs mgo-color-muted">Pay Group</dt><dd>{payRun.payGroupName}</dd></div>
            <div><dt className="mgo-text-xs mgo-color-muted">Pay Period</dt><dd>{payRun.payPeriodLabel || `${date(payRun.periodStartDate)}–${date(payRun.periodEndDate)}`}</dd></div>
            <div><dt className="mgo-text-xs mgo-color-muted">Run type</dt><dd>{formatSystemValue(payRun.runType)}</dd></div>
            <div><dt className="mgo-text-xs mgo-color-muted">Payment date</dt><dd>{date(payRun.paymentDate)}</dd></div>
            <div><dt className="mgo-text-xs mgo-color-muted">Period dates</dt><dd>{date(payRun.periodStartDate)}–{date(payRun.periodEndDate)}</dd></div>
            <div><dt className="mgo-text-xs mgo-color-muted">Run sequence</dt><dd>{payRun.runSequence}</dd></div>
          </dl>
        </section>
      </div>
    </Modal>
  );
};

export const CancelPayRunModal = ({
  payRun,
  visible,
  busy,
  actionAllowed,
  error,
  onConfirm,
  onClose,
}: CancelPayRunModalProps) => {
  useMangoDialogHeadingBridge(visible, 'cancel-pay-run-dialog');
  return (
    <Modal
      visible={visible}
      testId="cancel-pay-run-dialog"
      header="Cancel Pay Run?"
      size="small"
      role="alertdialog"
      ariaDescribedby="cancel-pay-run-description"
      closable={!busy}
      dismissableMask={!busy}
      closeOnEscape={!busy}
      onHide={() => { if (!busy) onClose(); }}
      footer={(
        <>
          <Button label="Keep Pay Run" variant="secondary" disabled={busy} onClick={onClose} />
          <Button label="Cancel Pay Run" variant="destructive" loading={busy} disabled={!actionAllowed} onClick={onConfirm} />
        </>
      )}
    >
      <div id="cancel-pay-run-description" className="mgo-stack mgo-gap-m">
        {error && <div role="alert"><Callout tone="critical" title="Pay Run could not be cancelled">{error}</Callout></div>}
        <p>
          Cancel <strong>{payRun.displayTitle}</strong>? The Pay Run will be retained with its audit details and
          shown as cancelled; it will not be deleted.
        </p>
        <p>This action cannot be undone through editing.</p>
      </div>
    </Modal>
  );
};
